import { ref } from "vue";

/**
 * Tamanho do painel do assistente:
 * - `compact`: janelinha flutuante no canto (abertura pelo balão);
 * - `large`: painel lateral de meia tela (botão Expandir e card de configuração
 *   do dashboard). Não existe tela cheia: a meia tela deixa o sistema visível ao lado.
 */
export type AssistantPanelSize = "compact" | "large";

/**
 * Pergunta que a própria TELA faz no chat, sem passar pelo modelo. Cada botão
 * tem o rótulo e a mensagem que o clique envia ao assistente.
 */
export interface LocalQuestion {
	text: string;
	options: { label: string; message: string }[];
	/**
	 * Quem responde DIGITANDO em vez de clicar: o texto vai depois disto, para o
	 * pedido não chegar ao modelo sem contexto (a pergunta só existe na tela).
	 */
	typedAnswerPrefix?: string;
}

// Estado compartilhado (singleton) para controlar o widget do assistente de fora
// dele — ex.: o card do dashboard abre o chat e inicia uma sessão guiada.
const isOpen = ref(false);
const seedPrompt = ref<string | null>(null);
const localQuestion = ref<LocalQuestion | null>(null);
const panelSize = ref<AssistantPanelSize>("compact");

export function useAssistant() {
	return {
		isOpen,
		seedPrompt,
		localQuestion,
		panelSize,
		open: () => {
			isOpen.value = true;
		},
		/** Fecha e volta ao compacto: a próxima abertura pelo balão não herda o tamanho. */
		close: () => {
			isOpen.value = false;
			panelSize.value = "compact";
		},
		/** Abre o assistente e envia automaticamente uma mensagem inicial. */
		sendPrompt: (text: string, options?: { size?: AssistantPanelSize }) => {
			seedPrompt.value = text;
			if (options?.size) panelSize.value = options.size;
			isOpen.value = true;
		},
		/**
		 * Abre o assistente com uma pergunta feita pela tela, com botões. Serve
		 * para o que não pode depender do modelo: a pergunta do modo da
		 * configuração guiada, que ele pulava num terreiro quase configurado.
		 */
		askLocally: (
			question: LocalQuestion,
			options?: { size?: AssistantPanelSize },
		) => {
			localQuestion.value = question;
			if (options?.size) panelSize.value = options.size;
			isOpen.value = true;
		},
		/**
		 * Troca de tela: a meia tela vai para o compacto, no canto, para não
		 * cobrir os botões da tela nova. As telas se ajustam pela largura da
		 * janela, não pelo espaço que sobra, então encolher a página ao lado não
		 * resolve — o cabeçalho da Corrente pede ~900px (teste de 16/10/2026).
		 */
		onNavigate: () => {
			panelSize.value = "compact";
		},
		/** Botão Expandir/Recolher: meia tela ↔ compacto. */
		toggleExpanded: () => {
			panelSize.value = panelSize.value === "large" ? "compact" : "large";
		},
	};
}
